import { useCallback, useEffect, useRef } from "react";

import {
  PRICING_REQUEST_POLLING_CAP_MS,
  PRICING_REQUEST_POLLING_INTERVAL_MS,
} from "@/constants/polling";
import { useTerminalPollingGate } from "@/hooks/useTerminalPollingGate";
import {
  useGetPricingRequestQuery,
  useLazyGetPricingRequestQuery,
} from "@generated/redux/services/generated/api";
import {
  type FilteredPricingEngineRequest,
  FilteredResolvedBlockchainWithdrawal,
  InstructionState,
  PaymentWithdrawalState,
  PricingQuoteState,
} from "@generated/types";

// Poll every 3s on the success page. The success page watches a payment move
// through its full lifecycle (SCHEDULED → ACCEPTED → FUNDS_CONFIRMED →
// PROCESSING → RECONCILED), which can take seconds to minutes. 3s balances
// responsiveness against needless load on the backend.

interface UsePricingPollingOptions {
  pricingRequestId: string | undefined;
  isFirstStep: boolean;
  isSuccessPage: boolean;
  shouldPollOnSuccess: boolean;
  onStopPolling: () => void;
  onResetPolling: () => void;
}

interface UsePricingPollingResult {
  pricingRequestData: FilteredPricingEngineRequest | undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  pricingRequestCustomError: any;
  /**
   * This pricing request has been read at least once — so an absent
   * `pricingRequestData` means "there is nothing to show", not "not yet".
   *
   * Needed because an error is NOT the only way this read comes back empty.
   * `fetchBaseQuery` resolves a zero-length body to `data: null` with no error
   * at all (`text.length ? JSON.parse(text) : null`), so a consumer watching
   * only the error flag cannot tell that case apart from still-loading and
   * renders a skeleton forever. See UI-175 / CUSTOMER-UI-6Y.
   *
   * Monotonic per id: once true it stays true across polls and retries, and
   * only resets when a DIFFERENT pricing request comes into view.
   */
  isPricingRequestSettled: boolean;
  /**
   * The success page stopped polling after PRICING_REQUEST_POLLING_CAP_MS
   * without reaching a final state, so what it shows may be out of date.
   * Information, not an error, and not "settled": the request itself is
   * unaffected and may still be read.
   */
  hasStoppedPolling: boolean;
  /** Read the pricing request now and resume polling with a new cap window. */
  restartPolling: () => void;
  fetchPricingRequestById: (
    id: string
  ) => Promise<FilteredPricingEngineRequest>;
}

/**
 * Encapsulates the RTK Query polling lifecycle for pricing requests.
 *
 * Responsibilities:
 * - Fetches pricing request by ID (skips on first step)
 * - Polls on success page every PRICING_REQUEST_POLLING_INTERVAL_MS until a terminal state
 * - Stops ONLY at payment/withdrawal terminals — RECONCILED or CANCELLED.
 *   CONFIRMED/ACCEPTED/FUNDS_CONFIRMED/PROCESSING are mid-flight states; the
 *   user expects the success page to reflect the true final state.
 * - Stops after PRICING_REQUEST_POLLING_CAP_MS without a terminal state and
 *   reports it (`hasStoppedPolling`); `restartPolling` resumes
 * - Resets polling flag when pricing request ID changes
 * - Provides manual fetch-by-ID for lazy queries
 */
export function usePricingPolling({
  pricingRequestId,
  isFirstStep,
  isSuccessPage,
  shouldPollOnSuccess,
  onStopPolling,
  onResetPolling: onResetPollingFlag,
}: UsePricingPollingOptions): UsePricingPollingResult {
  // The terminal-polling gate owns when the poll runs: only on the success
  // page while the request is still moving, never in the test env, and for at
  // most PRICING_REQUEST_POLLING_CAP_MS per pricing request.
  const {
    pollingInterval,
    update: updatePricingPoll,
    hasGivenUp: hasStoppedPolling,
    restart: restartPricingPoll,
  } = useTerminalPollingGate({
    intervalMs: PRICING_REQUEST_POLLING_INTERVAL_MS,
    capMs: PRICING_REQUEST_POLLING_CAP_MS,
  });

  const {
    data: pricingRequestData,
    error: pricingRequestCustomError,
    fulfilledTimeStamp,
  } = useGetPricingRequestQuery(
    { uuid: pricingRequestId! },
    {
      skip: !pricingRequestId || isFirstStep,
      pollingInterval,
      // If data is already cached in a terminal state (e.g., navigating
      // directly to the success page), avoid refetching on mount.
      refetchOnMountOrArgChange: false,
    }
  );

  // Off the success page there is nothing to wait for; on it, the terminal
  // walk below ends the wait by dispatching SET_SHOULD_POLL false.
  useEffect(() => {
    updatePricingPoll(!isSuccessPage || !shouldPollOnSuccess);
  }, [isSuccessPage, shouldPollOnSuccess, updatePricingPoll]);

  // A different pricing request is a different resource: it gets its own cap
  // window, and a stop on the previous one does not carry across. Called by
  // the reset sentinel below, exactly where the polling flag is reset.
  const onResetPolling = useCallback(() => {
    restartPricingPoll();
    onResetPollingFlag();
  }, [restartPricingPoll, onResetPollingFlag]);

  // Stop polling only when the payment/withdrawal reaches a true terminal
  // state. Per the state machine, that is RECONCILED or CANCELLED. Earlier
  // revisions also stopped at BOOKED/ACCEPTED (pricing), CONFIRMED/ACCEPTED
  // (instruction), and ACCEPTED/FUNDS_CONFIRMED (payment) — all mid-flight
  // states — which froze the success page before the payment actually settled.
  //
  // For exchange-only (trade) flows there is no payment object, so we fall
  // back to the pricing-quote terminal states (BOOKED / ACCEPTED) to ensure
  // polling still halts once the trade is locked in.
  // Depend on the specific primitive leaves we actually read, NOT the
  // `instructions` array reference — RTK Query returns a fresh array on every
  // poll response, so depending on the array would re-run this effect on
  // every 3s tick even when no state field changed.
  const tradeInstruction = pricingRequestData?.instructions?.[0];
  const payment = tradeInstruction?.deliveryInstruction?.payment as
    | (FilteredResolvedBlockchainWithdrawal & {
        paymentState?: PaymentWithdrawalState;
        resourceType?: string;
      })
    | undefined;
  const paymentState = payment?.paymentState;
  const withdrawalState = payment?.withdrawalState;
  const instructionState = payment?.instructionState;
  const resourceType = payment?.resourceType;
  const pricingState = pricingRequestData?.state as string | undefined;

  useEffect(() => {
    if (payment) {
      const isBlockchainWithdrawal = resourceType === "blockchain-withdrawals";
      const deliveryState = isBlockchainWithdrawal
        ? withdrawalState
        : paymentState;

      const reachedTerminal =
        deliveryState === PaymentWithdrawalState.Reconciled ||
        deliveryState === PaymentWithdrawalState.Cancelled ||
        instructionState === InstructionState.Cancelled;

      if (reachedTerminal) {
        onStopPolling();
      }
      return;
    }

    // Exchange-only flow (no payment/withdrawal attached).
    if (
      pricingState === PricingQuoteState.Booked ||
      pricingState === PricingQuoteState.Accepted ||
      pricingState === PricingQuoteState.Cancelled
    ) {
      onStopPolling();
    }
  }, [
    payment,
    paymentState,
    withdrawalState,
    instructionState,
    resourceType,
    pricingState,
    onStopPolling,
  ]);

  // Reset polling flag only when transitioning between two *defined* IDs.
  // Initialising to undefined ensures:
  //  - On mount (undefined → "pr_123"): no reset (prev was undefined)
  //  - On ID change ("pr_123" → "pr_456"): reset fires correctly
  // Without this, the reset effect overrides the stop effect on mount,
  // causing infinite polling on success pages with BOOKED state.
  const prevPricingRequestIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (
      pricingRequestId &&
      prevPricingRequestIdRef.current !== undefined &&
      prevPricingRequestIdRef.current !== pricingRequestId
    ) {
      onResetPolling();
    }
    prevPricingRequestIdRef.current = pricingRequestId;
  }, [pricingRequestId, onResetPolling]);

  // Latch "this pricing request has been read at least once", keyed by id.
  //
  // Deliberately NOT `!isFetching`: an empty-bodied 200 resolves to
  // `data: null` with no error, so no terminal state is ever reached and the
  // success page keeps polling every PRICING_REQUEST_POLLING_INTERVAL_MS
  // forever. A signal derived from `isFetching` would flip false on every
  // tick, so a consumer showing a recovery state for "settled but empty"
  // would oscillate back to a skeleton every 3s — and re-fire its telemetry
  // each cycle. The same applies to a user-initiated retry.
  //
  // Storing the id (rather than a boolean) makes the reset on navigation to a
  // different pricing request fall out for free: the new id has not settled
  // until its own read returns, with no second effect to order against.
  // Manual fetch by ID — memoized so the PricingContext value doesn't churn
  // every render (the context value lists this in its deps).
  const [triggerGetPricingRequest] = useLazyGetPricingRequestQuery();
  const fetchPricingRequestById = useCallback(
    async (id: string): Promise<FilteredPricingEngineRequest> => {
      const result = await triggerGetPricingRequest({ uuid: id }).unwrap();
      return result as unknown as FilteredPricingEngineRequest;
    },
    [triggerGetPricingRequest]
  );

  // "Check again" after the cap: read now through the same cache entry the
  // poll subscribes to (the restarted poll's first tick is an interval away),
  // then poll again under a new cap window. A failed read lands on the cache
  // entry's error like any poll, so there is nothing to catch here.
  const restartPolling = useCallback(() => {
    restartPricingPoll();
    if (pricingRequestId) {
      void triggerGetPricingRequest({ uuid: pricingRequestId });
    }
  }, [restartPricingPoll, pricingRequestId, triggerGetPricingRequest]);

  return {
    pricingRequestData: pricingRequestData as
      | FilteredPricingEngineRequest
      | undefined,
    pricingRequestCustomError,
    // `fulfilledTimeStamp` is deliberate, and `!isFetching` is deliberately
    // NOT used. RTK's `writePendingCacheEntry` leaves this field alone, so it
    // is monotonic: once a read has come back it stays set across every later
    // poll and retry. `isFetching` flips false→true on every 3s poll tick, and
    // an empty-bodied 200 never reaches a terminal state so polling never
    // stops — a consumer keyed on it would oscillate between its empty-state
    // and a skeleton forever, re-firing telemetry each cycle.
    isPricingRequestSettled: fulfilledTimeStamp !== undefined,
    hasStoppedPolling,
    restartPolling,
    fetchPricingRequestById,
  };
}
